import dynamic from 'next/dynamic'
import { useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'

import classnames from 'classnames'

import AnimateIntoView from '~/components/atoms/AnimateIntoView/AnimateIntoView'
import type { ArtworkCardProps } from '~/components/atoms/ArtworkCard/ArtworkCard'
import ArtworkCard from '~/components/atoms/ArtworkCard/ArtworkCard'
import Button, { ButtonVariants } from '~/components/atoms/Button/Button'
import { Col, Grid, Row } from '~/components/atoms/Grid'
import Loader from '~/components/atoms/Loader/Loader'
import Spacer, { SpacerSizes } from '~/components/atoms/Spacer/Spacer'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import VerticalPadder from '~/components/atoms/VerticalPadder/VerticalPadder'
import type { TSearchAnalystEventProps } from '~/services/elastic/analyst'
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'

import type { ArtworksListProps, filtersType } from './ArtworksList'
import useHooks from './hooks'

import styles from './ArtworksList.module.css'

import RecordTypes = indexes.RecordTypes
import { PushInteractionLocation } from '~/utils/posthogEvents'

const TradingFloorList = dynamic(
  () => import('../TradingFloorList/TradingFloorList')
)

export interface ArtWorksProps extends ArtworksListProps {
  filters?: filtersType
  pagenumber?: number
  slugParams?: boolean
  setFacets?: (facets: any, total: number) => void
  searchTerm?: string
  setIsloading?: (isLoading: boolean) => void
  fallBackPaginationHandler?: () => any
  searchInsightsEvents?: {
    artwork: TSearchAnalystEventProps
    artist?: TSearchAnalystEventProps
  }
}

export default function Artworks({
  queryId,
  fallbackData,
  type,
  filters,
  pagenumber = 1,
  slugParams,
  setFacets,
  searchTerm = '',
  setIsloading,
  fallBackPaginationHandler,
  searchInsightsEvents,
  pageSize,
  noArtistLink,
}: ArtWorksProps) {
  const t = useTranslations('common')
  const {
    isValidating,
    insightsQueryID,
    size,
    artworks,
    isEmpty,
    isLoadingMore,
    isLoadingInitialData,
    total,
    loadMoreHandler,
    nextPageUrl,
    data,
    artworksElRef,
    tradingView,
    handleSearchResultsClicked,
  } = useHooks(
    queryId,
    type,
    fallbackData,
    filters,
    searchTerm,
    slugParams,
    setFacets,
    setIsloading,
    pagenumber,
    pageSize
  )

  return (
    <div
      className={classnames(
        styles.artworksWrap,
        fallbackData && isValidating && size === 1 && styles.artworksWrapLoading
      )}
      ref={artworksElRef}
    >
      <VerticalPadder noPaddingTop={true}>
        {artworks && artworks.length > 0 && (
          <>
            {!filters?.tradingFloor && !tradingView ? (
              <Grid>
                <Row>
                  {artworks.map((item: ArtworkCardProps, i: number) => {
                    return (
                      <Col
                        variant="col-1/2--sm col-1/3--md col-1/4--hg"
                        key={`artworkcard-${item.title}${i}`}
                      >
                        <AnimateIntoView>
                          <ArtworkCard
                            {...item}
                            insightsQueryID={insightsQueryID}
                            itemIndex={i + 1}
                            noArtistLink={noArtistLink}
                            searchInsightsEvents={searchInsightsEvents}
                            handleAnalytics={() => {
                              handleSearchResultsClicked(
                                searchTerm,
                                {
                                  searchClickCategory: RecordTypes.ARTWORK,
                                  positionalIndex: i,
                                  searchClickArtist: item.artist,
                                  searchClickArtwork: item.title,
                                },
                                PushInteractionLocation.InnerPageSearch
                              )
                            }}
                          />
                          <Spacer size={SpacerSizes.lg} />
                        </AnimateIntoView>
                      </Col>
                    )
                  })}
                </Row>
              </Grid>
            ) : (
              <div className={styles.tradingView}>
                {artworks[0].ticketStatus && (
                  <AnimateIntoView>
                    <TradingFloorList items={artworks} noContainer />
                  </AnimateIntoView>
                )}
              </div>
            )}
          </>
        )}

        {isEmpty && !isLoadingMore && (
          <Text Tag="p" size={TextSizes.bodyLrg} className={styles.zeroResults}>
            {t('noArtworksFound')}
          </Text>
        )}

        {(isLoadingMore || isLoadingInitialData) && <Loader />}

        {artworks?.length < total && (
          <div className={styles.btnWrap}>
            <Button
              iconLeft
              preventDefault
              testAttr="load-more-artworks"
              linkOnclick={e => {
                loadMoreHandler?.(e)
                multiPostHogEventPusher([
                  {
                    eventType: 'Artworks: Load more clicked',
                  },
                ])
              }}
              link={nextPageUrl}
              variant={ButtonVariants.secondaryDark}
            >
              {t('loadMore')}
            </Button>
          </div>
        )}

        {!data && fallBackPaginationHandler && fallBackPaginationHandler()}
      </VerticalPadder>
    </div>
  )
}
